import { useContext, useEffect, useState, type FunctionComponent } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { track, trackPageLoad } from '@fff-web/fff-analytics';
import { Button, ButtonLink, showSuccessToast } from '@fff-web/fff-ui-shared';
import breakpoints from '../../utils/breakpoints';
import buttonLabels from '../../utils/buttonLabels';
import { pages } from '../../utils/pages';
import { redirectToContactUsSwap, redirectToFAQ } from '../../utils/redirectMethods';
import toastText from '../../utils/toastText';
import AfterConfirmText from '../commons/after-confirm-text';
import BreakLine from '../commons/break-line';
import SwapConfirmation from '../commons/swap-confirmation';
import useCurrentWidth from '../commons/use-current-width/useCurrentWidth';
import useSwapMainText from '../commons/use-swap-main-text/useSwapMainText';
import { DynamicTextContext } from '../context/DynamicTextContext';
import SeeDetailsModal from '../see-details-modal';
import SwapConfirmationModal from '../swap-confirmation-modal/SwapConfirmationModal';
import mobileImage from '../../assets/images/swap-my-box/swap-my-box-mobile.jpg';
import * as styles from './SwapMyBox.module.css';

const ACTIVATE_POP_UP = {
  trackName: 'Activate Pop_up',
  popUpType: 'click_open',
  popUpName: 'Swap Learn More',
};

const SwapMyBox: FunctionComponent = () => {
  const { dynamicText } = useContext(DynamicTextContext);
  const { swapForCreditCancelFlowApi } = useFlags();
  const [hasSwapped, setHasSwapped] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const currentWidth = useCurrentWidth();
  const isDesktop = currentWidth > breakpoints.medium;
  const swapMainTexts = useSwapMainText();

  useEffect(() => {
    document.title = pages.SWAP_FOR_CREDIT.title;
    dynamicText?.wooUserId && trackPageLoad();
  }, [dynamicText?.wooUserId]);

  const handleSeeDetails = () => {
    track({
      trackName: ACTIVATE_POP_UP.trackName,
      properties: {
        cta_name: buttonLabels.SEE_DETAILS,
        pop_up_type: ACTIVATE_POP_UP.popUpType,
        pop_up_name: ACTIVATE_POP_UP.popUpName,
      },
    });
    setShowDetails(true);
  };

  const handleSwapForCredit = () => {
    if (!isDesktop) {
      setShowConfirmation(true);
    } else {
      setShowConfirmationModal(true);
    }
  };

  const closeDetailModal = () => {
    setShowDetails(false);
  };

  const closeSwapConfirmationModal = () => setShowConfirmationModal(false);

  const swapForCreditButton = () => (
    <div className={styles.swapForCredit}>
      <Button onClick={() => handleSwapForCredit()}>{buttonLabels.SWAP_FOR_CREDIT}</Button>
    </div>
  );

  const oldSkipMyBox = () => {
    if (hasSwapped) return <AfterConfirmText />;
    return swapForCreditButton();
  };

  const newSwapForCredit = () => {
    if (hasSwapped) {
      return (
        <div className={styles.contactToUnswap}>
          <Button variation="primary-outline" onClick={() => redirectToContactUsSwap()}>
            {buttonLabels.CONTACT_US_TO_UNSWAP}
          </Button>
        </div>
      );
    }
    return swapForCreditButton();
  };

  if (showConfirmation && !isDesktop) {
    return (
      <SwapConfirmation
        setShowConfirmation={setShowConfirmation}
        setHasSwapped={setHasSwapped}
        toastOnSwapConfirmation={() =>
          showSuccessToast(toastText.success.TITLE, toastText.success.SUBTITLE)
        }
      />
    );
  }

  return (
    <div className={styles.swapMyBoxContainer}>
      {showConfirmationModal && (
        <SwapConfirmationModal
          closeAction={closeSwapConfirmationModal}
          setShowConfirmationModal={setShowConfirmationModal}
          setHasSwapped={setHasSwapped}
        />
      )}
      {showDetails && <SeeDetailsModal closeAction={closeDetailModal} />}
      <div className={styles.mainContentContainer}>
        <div className={styles.mainContent}>
          <h1 className={styles.title}>Swap for Credit</h1>
          <p className={styles.disclaimer}>
            Swap your box for credit and create your own shopping experience - so you decide how to
            shop with us!
          </p>
          {!isDesktop && <img className={styles.mobileImage} alt="fabFitFun" src={mobileImage} />}

          <ul className={styles.mainText}>
            {swapMainTexts.map((t, index) => {
              const liKey = `smb-li-${index}`;
              return (
                <li key={liKey} className={styles.mainTextItem}>
                  {t}
                </li>
              );
            })}
          </ul>

          <div className={styles.actionsContainer}>
            {swapForCreditCancelFlowApi ? newSwapForCredit() : oldSkipMyBox()}
            <div className={styles.seeDetails}>
              <ButtonLink type="gray" label={buttonLabels.SEE_DETAILS} onClick={handleSeeDetails} />
            </div>
          </div>
        </div>
      </div>

      {!isDesktop && <BreakLine />}

      <h1 className={styles.faqTitle}>FAQs</h1>
      <div className={styles.faqContainer}>
        <p className={styles.faqQuestion}>What is Unlimited Swap for Credit?</p>
        <p className={styles.faqAnswer}>
          We’re making your membership even more flexible by giving you unlimited swap for credit.
          Receive credit that you can spend on any future FabFitFun purchases except membership fees
          and gift cards. Think of it as a flexible experience that allows you to shop how you like,
          where you like, and when you like.
        </p>

        {dynamicText?.faqCreditAmount ? (
          <>
            <p className={styles.faqQuestion}>How soon will I get my credit?</p>
            <p className={styles.faqAnswer}>{dynamicText?.faqCreditAmount}</p>
          </>
        ) : null}

        <p className={styles.faqQuestion}>How often can I Swap?</p>
        <p className={styles.faqAnswer}>Swap an unlimited amount of times.</p>
      </div>

      <p className={styles.stillQuestions}>Still Have Questions?</p>
      <div className={styles.visitFaq}>
        <Button variation="black" onClick={() => redirectToFAQ()}>
          {buttonLabels.VISIT_FAQ}
        </Button>
      </div>
    </div>
  );
};

export default SwapMyBox;
